<script setup>
import { useStore } from "vuex";
import debounce from "lodash.debounce";
import { diff } from "deep-object-diff";
import { computed, onMounted, ref, watch, onUnmounted, nextTick } from "vue";
import ScrollToElement from "common/utils/ui/scroll-to-element";
import ArtworkDesign from "./ArtworkDesign.vue";
import ArtworkMeasurement from "./ArtworkMeasurement.vue";

const store = useStore();
const surface = computed(() => store.getters["artwork/surface"]);
const getSurface = surfaceLabel => store.getters["artwork/getSurface"](surfaceLabel);
const surfaceLabel = computed(() => store.getters["artwork/surfaceLabel"]);
const artworkExists = computed(() => store.getters["artwork/artworkExists"]);
const downscaleFactor = computed(() => store.getters["artwork/downscaleFactor"]);
const isResizableMode = ref(false);
const surfaceReference = ref();
const canvasReference = ref();
const boxReference = ref();
const canvasStyles = ref({});
const isLoadedSurface = ref(false);
const boundingBoxStyles = computed(() => {
  const { top, left, width, height } = surface.value.boundingBox;
  return {
    top: `${top * downscaleFactor.value}px`,
    left: `${left * downscaleFactor.value}px`,
    width: `${width * downscaleFactor.value}px`,
    height: `${height * downscaleFactor.value}px`
  };
});

const updateResize = () => {
  isResizableMode.value = true;
};

const endResize = () => {
  isResizableMode.value = false;
};

const initSurface = () =>
  new Promise(resolve => {
    isLoadedSurface.value = false;
    const image = new Image();
    image.src = surface.value.picture;
    image.onload = () => {
      canvasStyles.value = {};

      nextTick(() => {
        const { width: canvasWidth, height: canvasHeight } = surfaceReference.value;

        const { width, height, top, left } =
          surfaceLabel.value === "frontLeftChest"
            ? getSurface("front").boundingBox
            : surface.value.boundingBox;

        const newDownscaleFactor = Math.min(canvasWidth / width, canvasHeight / height);
        const offsetTop = (top * newDownscaleFactor) / 2;

        canvasStyles.value = {
          width: `${image.width * newDownscaleFactor}px`,
          top: `${-offsetTop}px`,
          left: `${-left * newDownscaleFactor}px`,
          height: `${offsetTop + height * newDownscaleFactor}px`
        };

        store.commit("artwork/setDownscaleFactor", newDownscaleFactor);
        isLoadedSurface.value = true;
        resolve();
      });
    };
  });

let prevInnerWidth;
const resizeSurface = debounce(
  () => {
    const currentInnerWidth = window.innerWidth;
    if (currentInnerWidth === prevInnerWidth) return;
    initSurface();
    prevInnerWidth = currentInnerWidth;
  },
  1000 // 1 second debounce
);

watch(surface, async (newVal, oldVal) => {
  const diffArray = Object.keys(diff(oldVal, newVal));
  if (diffArray.length === 0 || (diffArray.length === 1 && diffArray.includes("picture"))) return;
  await initSurface();

  const isDesktop = window.matchMedia("(min-width: 992px)").matches;
  if (!isDesktop) {
    await nextTick(() => {
      ScrollToElement.scrollToElement(
        canvasReference.value,
        window.innerHeight - canvasReference.value.offsetHeight - 160, // 160 is the height position controls below which should be also visible
        {
          behavior: "instant"
        }
      );
    });
  }
});
onMounted(() => {
  initSurface();
  window.addEventListener("resize", resizeSurface);
});
onUnmounted(() => {
  window.removeEventListener("resize", resizeSurface);
});
</script>

<template>
  <div ref="canvasReference" class="artwork-canvas">
    <div class="artwork-canvas__height-wrapper" :style="canvasStyles">
      <img
        ref="surfaceReference"
        :src="surface.picture"
        class="artwork-canvas__canvas"
        alt="Artwork canvas"
      />
      <template v-if="artworkExists">
        <ArtworkMeasurement
          :get-surface="getSurface"
          :surface-label="surfaceLabel"
          :downscale-factor="downscaleFactor"
        />

        <div
          ref="boxReference"
          class="artwork-canvas__bounding-box"
          :class="{
            'artwork-canvas__bounding-box--is-resizable-mode': isResizableMode
          }"
          :style="boundingBoxStyles"
        >
          <ArtworkDesign
            v-if="isLoadedSurface"
            @end:resize="endResize"
            @update:resize="updateResize"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/mixin";
@import "frontend/init/pod/settings";

.artwork-canvas {
  overflow: hidden;
  padding: 0 50px 50px;
  background: $white;

  &__height-wrapper {
    position: relative;
  }

  &__canvas {
    pointer-events: none;
    user-select: none;
  }

  &__bounding-box {
    position: absolute;

    &--is-resizable-mode {
      outline: 1px dashed grey;
    }
  }
}
</style>
